import {
  RegisteredRouter,
  ValidateNavigateOptions,
  useNavigate,
  Navigate,
  useParams,
} from '@tanstack/react-router';
import { useCallback } from 'react';
import styled from 'styled-components';

import { UrlWarehouseProvider } from 'common/context/WarehouseContext';

import { NavigationBar } from '../common/components/cookbook/NavigationBar/NavigationBar';
import { Typography as T } from '../common/components/cookbook/Typography/Typography';
import { ViewMeta } from '../common/components/cookbook/ViewMeta';
import { useFormatMessage } from '../common/hooks/useFormatMessage';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const WarehouseSelectionPrompt: React.FC = () => {
  const formatMessage = useFormatMessage();
  return (
    <Root data-test-id="warehouse-selection-prompt">
      <T.subheadline>{formatMessage('components.warehouse_selection_prompt.title')}</T.subheadline>
    </Root>
  );
};

type Props = {
  routeWithWarehouseId: ValidateNavigateOptions<RegisteredRouter>['to'];
  routeWithoutWarehouseId: ValidateNavigateOptions<RegisteredRouter>['to'];
  children?: React.ReactNode;
};

/**
 * Show a warehouse picker if no warehouse is selected, otherwise render the children.
 * @param onWarehouseChange Callback for when a warehouse is selected in the warehouse picker. Can also receive undefined if the warehouse is "deselected", which usually happens if the warehouse ID is invalid somehow.
 * @param children It's possible to pass a function as children to render the content with the selected warehouse, which is useful for rendering e.g. a `<Navigate>` component. Also receives a `Venue` object.
 */
const RequireWarehouse = ({ children, routeWithWarehouseId, routeWithoutWarehouseId }: Props) => {
  const formatMessage = useFormatMessage();
  const selectWarehouseLabel = formatMessage('components.require_warehouse_selection.title');
  const navigate = useNavigate();
  const { warehouseId: warehouseIdInUrl } = useParams({ strict: false });

  const onWarehouseChange = useCallback(
    (warehouseId?: string) => {
      const target = warehouseId
        ? { to: routeWithWarehouseId, params: { warehouseId } as any }
        : { to: routeWithoutWarehouseId };
      navigate(target);
    },
    [navigate, routeWithWarehouseId, routeWithoutWarehouseId],
  );

  return (
    <UrlWarehouseProvider
      warehouseId={warehouseIdInUrl}
      onWarehouseChange={onWarehouseChange}
      onInvalidWarehouseId={() => {
        navigate({ to: routeWithoutWarehouseId });
      }}
      fallback={
        <ViewMeta title={selectWarehouseLabel}>
          <NavigationBar title={selectWarehouseLabel} showWarehouseSelector />
          <WarehouseSelectionPrompt />
        </ViewMeta>
      }
    >
      {(warehouse) =>
        warehouse.id !== warehouseIdInUrl ? (
          // a discrepancy here currently means we want to navigate to the warehouse selection persisted in localStorage
          <Navigate to={routeWithWarehouseId} params={{ warehouseId: warehouse?.id } as any} />
        ) : (
          children
        )
      }
    </UrlWarehouseProvider>
  );
};

export default RequireWarehouse;
